import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
// @ts-ignore
import TomSelect from 'tom-select';
import 'tom-select/dist/css/tom-select.bootstrap5.css';
import './SearchFilters.css';

interface FilterOption {
    id: number;
    name: string;
    name_ar?: string;  // Arabic name for localization
}

// Domain configuration - which fields and doc_types are available for each domain
const domainConfig: Record<string, {
    docTypes: string[];
    showFields: {
        codes: boolean;
        dates: boolean;
        juridiction: boolean;
        chambre: boolean;
        nature: boolean;
    };
}> = {
    code_articles: {
        docTypes: ['loi', 'ordonnance', 'decret'],
        showFields: { codes: true, dates: false, juridiction: false, chambre: false, nature: false }
    },
    constitution: {
        docTypes: [],
        showFields: { codes: false, dates: false, juridiction: false, chambre: false, nature: false }
    },
    jurisprudences: {
        docTypes: ['arret'],
        showFields: { codes: false, dates: true, juridiction: true, chambre: true, nature: false }
    },
    joarticles: {
        docTypes: ['loi', 'ordonnance', 'decret'],
        showFields: { codes: false, dates: false, juridiction: false, chambre: false, nature: true }
    },
    livres: {
        docTypes: ['doctrine'],
        showFields: { codes: false, dates: false, juridiction: false, chambre: false, nature: false }
    },
    theses: {
        docTypes: ['doctrine'],
        showFields: { codes: false, dates: false, juridiction: false, chambre: false, nature: false }
    }
};

// All document types with labels
const allDocumentTypes = [
    { value: 'loi', label: 'Loi', labelAr: 'قانون' },
    { value: 'ordonnance', label: 'Ordonnance', labelAr: 'أمر' },
    { value: 'decret', label: 'Décret', labelAr: 'مرسوم' },
    { value: 'arret', label: 'Arrêt', labelAr: 'قرار' },
    { value: 'doctrine', label: 'Doctrine', labelAr: 'فقه' }
];

// Domain options with labels
const domainOptions = [
    { value: 'code_articles', label: 'Codes juridiques', labelAr: 'مواد القانون' },
    { value: 'joarticles', label: 'Textes officiels', labelAr: 'الجريدة الرسمية' },
    { value: 'jurisprudences', label: 'Jurisprudence', labelAr: 'الاجتهاد القضائي' }
];

const SearchFilters: React.FC = () => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const isArabic = i18n.language === 'ar';

    // Read params from URL or location state
    const stateParams = location.state?.searchParams ? new URLSearchParams(location.state.searchParams) : null;
    const currentParams = searchParams.size > 0 ? searchParams : (stateParams || searchParams);

    // Filter states from URL
    const query = currentParams.get('q') || '';

    // Determine the domain. On a detail page, the path is authoritative (the
    // doc type cannot be anything else), so derive from path and ignore any
    // possibly-stale ?domain= URL param. Elsewhere (e.g. /search), use the
    // URL param.
    let pathDomain: string | null = null;
    if (location.pathname.includes('/detail/jurisprudence')) pathDomain = 'jurisprudences';
    else if (location.pathname.includes('/detail/jo')) pathDomain = 'joarticles';
    else if (location.pathname.includes('/detail/code-article')) pathDomain = 'code_articles';
    else if (location.pathname.includes('/detail/theses')) pathDomain = 'theses';
    else if (location.pathname.includes('/detail/livres')) pathDomain = 'livres';

    const domain = pathDomain || currentParams.get('domain') || 'code_articles';
    const docType = currentParams.get('doc_type') || '';
    const codeId = currentParams.get('code_id') || '';
    const dateStart = currentParams.get('date_start') || '';
    const dateEnd = currentParams.get('date_end') || '';
    const joSignatureDateStart = currentParams.get('jo_signature_date_start') || '';
    const joSignatureDateEnd = currentParams.get('jo_signature_date_end') || '';
    const joYear = currentParams.get('jo_year') || '';
    const joPublicationDateStart = currentParams.get('jo_publication_date_start') || '';
    const joPublicationDateEnd = currentParams.get('jo_publication_date_end') || '';
    const typeParam = currentParams.get('type') || 'corpus';
    const joJournalNum = currentParams.get('jo_journal_num') || '';
    const joTextNum = currentParams.get('jo_text_num') || '';
    const juridictionId = currentParams.get('juridiction_id') || '';
    const chambreId = currentParams.get('chambre_id') || '';
    const natureIdParams = currentParams.get('nature_id');
    const natureIds = natureIdParams ? natureIdParams.split(',').map(Number).filter(n => !isNaN(n)) : [];
    const wholeWord = currentParams.get('whole_word') === 'true';
    const anyWord = currentParams.get('any_word') === 'true';
    const strictSearch = currentParams.get('strict_search') === 'true';
    const searchField = currentParams.get('search_field') || 'title'; // 'title', 'content'

    // Local states for form inputs
    const [localQuery, setLocalQuery] = useState(query);
    const [selectedDomain, setSelectedDomain] = useState(domain);
    const [selectedDocType, setSelectedDocType] = useState(docType);
    const [selectedCode, setSelectedCode] = useState(codeId);
    const [codePdfUrl, setCodePdfUrl] = useState<string | null>(null);
    const [localDateStart, setLocalDateStart] = useState(dateStart);
    const [localDateEnd, setLocalDateEnd] = useState(dateEnd);
    const [localJoSignatureDateStart, setLocalJoSignatureDateStart] = useState(joSignatureDateStart);
    const [localJoSignatureDateEnd, setLocalJoSignatureDateEnd] = useState(joSignatureDateEnd);
    const [localJoYear, setLocalJoYear] = useState(joYear);
    const [localJoPublicationDateStart, setLocalJoPublicationDateStart] = useState(joPublicationDateStart);
    const [localJoPublicationDateEnd, setLocalJoPublicationDateEnd] = useState(joPublicationDateEnd);
    const [selectedType, setSelectedType] = useState(typeParam);
    const [localJoJournalNum, setLocalJoJournalNum] = useState(joJournalNum);
    const [localJoTextNum, setLocalJoTextNum] = useState(joTextNum);
    const [selectedJuridiction, setSelectedJuridiction] = useState(juridictionId);
    const [selectedChambre, setSelectedChambre] = useState(chambreId);
    const [selectedNatures, setSelectedNatures] = useState<number[]>(natureIds);
    const [isWholeWord, setIsWholeWord] = useState(wholeWord);
    const [isAnyWord, setIsAnyWord] = useState(anyWord);
    const [isStrictSearch, setIsStrictSearch] = useState(strictSearch);
    const [selectedSearchField, setSelectedSearchField] = useState(searchField);

    // Dynamic options
    const [availableCodes, setAvailableCodes] = useState<FilterOption[]>([]);
    const [availableJuridictions, setAvailableJuridictions] = useState<FilterOption[]>([]);
    const [availableChambres, setAvailableChambres] = useState<FilterOption[]>([]);
    const [availableNatures, setAvailableNatures] = useState<FilterOption[]>([]);
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Bouton « eBook du code » : seulement si l'API délivre un lien (abonné payant
    // connecté + PDF dans la langue courante). Essai gratuit / anonyme : pas de bouton.
    useEffect(() => {
        if (!selectedCode || selectedDomain !== 'code_articles' || !localStorage.getItem('token')) {
            setCodePdfUrl(null);
            return;
        }
        let cancelled = false;
        api.get(`/ebook/code/${selectedCode}`, { params: { lang: i18n.language === 'ar' ? 'ar' : 'fr' } })
            .then(res => { if (!cancelled) setCodePdfUrl(res.data?.url || null); })
            .catch(() => { if (!cancelled) setCodePdfUrl(null); });
        return () => { cancelled = true; };
    }, [selectedCode, selectedDomain, i18n.language]);

    // Get current domain config
    const currentConfig = useMemo(() => {
        return domainConfig[selectedDomain] || domainConfig.code_articles;
    }, [selectedDomain]);

    // Get filtered document types for current domain
    const filteredDocTypes = useMemo(() => {
        if (currentConfig.docTypes.length === 0) return [];
        return allDocumentTypes.filter(dt => currentConfig.docTypes.includes(dt.value));
    }, [currentConfig]);

    // Sync states from URL on change
    useEffect(() => {
        setLocalQuery(query);
        setSelectedDomain(domain);
        setSelectedDocType(docType);
        setSelectedCode(codeId);
        setLocalDateStart(dateStart);
        setLocalDateEnd(dateEnd);
        setLocalJoSignatureDateStart(joSignatureDateStart);
        setLocalJoSignatureDateEnd(joSignatureDateEnd);
        setLocalJoYear(joYear);
        setLocalJoPublicationDateStart(joPublicationDateStart);
        setLocalJoPublicationDateEnd(joPublicationDateEnd);
        setSelectedType(typeParam);
        setLocalJoJournalNum(joJournalNum);
        setLocalJoTextNum(joTextNum);
        setSelectedJuridiction(juridictionId);
        setSelectedChambre(chambreId);
        setSelectedNatures(natureIds);
        setIsWholeWord(wholeWord);
        setIsAnyWord(anyWord);
        setIsStrictSearch(strictSearch);
        setSelectedSearchField(searchField);
    }, [query, domain, docType, codeId, dateStart, dateEnd, joSignatureDateStart, joSignatureDateEnd, joYear, joPublicationDateStart, joPublicationDateEnd, typeParam, joJournalNum, joTextNum, juridictionId, chambreId, natureIdParams, wholeWord, anyWord, strictSearch, searchField]);

    // Reset doc_type when domain changes (if current selection is not valid)
    useEffect(() => {
        if (selectedDocType && !currentConfig.docTypes.includes(selectedDocType)) {
            setSelectedDocType('');
        }
    }, [selectedDomain, selectedDocType, currentConfig.docTypes]);

    // Fetch filter options on mount
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const [codesRes, jurisdictionsRes, naturesRes] = await Promise.all([
                    api.post('/search/filters', { doc_type: 'code_article' }),
                    api.post('/search/filters', { doc_type: 'jps' }),
                    api.post('/search/filters', { doc_type: 'joarticles' })
                ]);

                if (codesRes.data?.codes) {
                    setAvailableCodes(codesRes.data.codes);
                }
                if (jurisdictionsRes.data?.juridictions) {
                    setAvailableJuridictions(jurisdictionsRes.data.juridictions);
                }
                if (jurisdictionsRes.data?.chambres) {
                    setAvailableChambres(jurisdictionsRes.data.chambres);
                }
                if (naturesRes.data?.natures) {
                    setAvailableNatures(naturesRes.data.natures);
                }
            } catch (err) {
                console.error('Error fetching filters:', err);
            }
        };
        fetchFilters();
    }, []);



    // Initialize TomSelect for Nature filter
    useEffect(() => {
        let tomSelectInstance: any = null;

        // Only initialize if we have natures and the element exists
        if (currentConfig.showFields.nature && availableNatures.length > 0) {
            const selectElement = document.getElementById('nature-select') as HTMLSelectElement;

            if (selectElement) {
                // Destroy previous instance if it exists (though useEffect cleanup should handle this)
                if ((selectElement as any).tomselect) {
                    (selectElement as any).tomselect.destroy();
                }

                tomSelectInstance = new TomSelect(selectElement, {
                    plugins: ['remove_button', 'checkbox_options'],
                    maxItems: null,
                    valueField: 'value',
                    labelField: 'text',
                    searchField: ['text'],
                    create: false,
                    options: availableNatures.map(n => ({
                        value: n.id,
                        text: isArabic && n.name_ar ? n.name_ar : n.name
                    })),
                    items: selectedNatures.map(String),
                    render: {
                        option: function (data: any, escape: any) {
                            return '<div>' + escape(data.text) + '</div>';
                        },
                        item: function (data: any, escape: any) {
                            return '<div>' + escape(data.text) + '</div>';
                        }
                    },
                    onItemAdd: function () {
                        // Sync state when item added
                        const values = this.getValue(); // Returns array of strings or string
                        const ids = Array.isArray(values) ? values.map(Number) : [Number(values)];
                        setSelectedNatures(ids.filter(n => !isNaN(n)));
                    },
                    onItemRemove: function () {
                        // Sync state when item removed
                        const values = this.getValue();
                        const ids = Array.isArray(values) ? values.map(Number) : [Number(values)];
                        // If empty, values might be empty array or null depending on version
                        setSelectedNatures(ids.filter(n => !isNaN(n)));
                    }
                });

                // No need to set value separately if we pass 'items' in config, 
                // but setValue with silent=true is safer to ensure sync
                if (selectedNatures.length > 0) {
                    tomSelectInstance.setValue(selectedNatures.map(String), true);
                }
            }
        }


        return () => {
            if (tomSelectInstance) {
                tomSelectInstance.destroy();
            }
        };
        // Re-run when these deps change. Note: selectedNatures is NOT a dep to avoid loop, 
        // unless we need to sync external updates back to TomSelect (e.g. reset)
    }, [currentConfig.showFields.nature, availableNatures, isArabic]);

    // Sync external changes to selectedNatures (like Reset) to TomSelect
    useEffect(() => {
        const selectElement = document.getElementById('nature-select') as any;
        if (selectElement && selectElement.tomselect) {
            const currentValues = selectElement.tomselect.getValue();
            const currentIds = Array.isArray(currentValues) ? currentValues.map(Number) : [];

            // Check if state matches UI
            const stateIds = selectedNatures.slice().sort();
            const uiIds = currentIds.slice().sort();

            const isEqual = stateIds.length === uiIds.length && stateIds.every((val, index) => val === uiIds[index]);

            if (!isEqual) {
                selectElement.tomselect.setValue(selectedNatures.map(String), true);
            }
        }
    }, [selectedNatures]);

    // Initialize TomSelect for Code Juridique filter
    useEffect(() => {
        let tomSelectInstance: any = null;

        if (currentConfig.showFields.codes && availableCodes.length > 0) {
            const selectElement = document.getElementById('code-select') as HTMLSelectElement;

            if (selectElement) {
                if ((selectElement as any).tomselect) {
                    (selectElement as any).tomselect.destroy();
                }

                tomSelectInstance = new TomSelect(selectElement, {
                    plugins: ['clear_button'],
                    maxItems: 1,
                    valueField: 'value',
                    labelField: 'text',
                    searchField: ['text'],
                    create: false,
                    allowEmptyOption: true,
                    options: [
                        { value: '', text: isArabic ? 'جميع القوانين' : 'Tous les codes' },
                        ...availableCodes.map(c => ({
                            value: String(c.id),
                            text: isArabic && c.name_ar ? c.name_ar : c.name
                        }))
                    ],
                    items: selectedCode ? [selectedCode] : [''],
                    render: {
                        option: function (data: any, escape: any) {
                            return '<div>' + escape(data.text) + '</div>';
                        },
                        item: function (data: any, escape: any) {
                            return '<div>' + escape(data.text) + '</div>';
                        }
                    },
                    onChange: function (value: string) {
                        setSelectedCode(value);
                    }
                });
            }
        }

        return () => {
            if (tomSelectInstance) {
                tomSelectInstance.destroy();
            }
        };
    }, [currentConfig.showFields.codes, availableCodes, isArabic]);

    // Sync external changes to selectedCode (like Reset) to TomSelect
    useEffect(() => {
        const selectElement = document.getElementById('code-select') as any;
        if (selectElement && selectElement.tomselect) {
            const currentValue = selectElement.tomselect.getValue();
            if (currentValue !== selectedCode) {
                selectElement.tomselect.setValue(selectedCode || '', true);
            }
        }
    }, [selectedCode]);

    // Apply all filters
    const applyFilters = () => {
        const newParams = new URLSearchParams();

        if (localQuery) newParams.set('q', localQuery);
        if (selectedDomain) newParams.set('domain', selectedDomain);
        if (selectedDocType) newParams.set('doc_type', selectedDocType);
        if (isWholeWord) newParams.set('whole_word', 'true');
        if (isAnyWord) newParams.set('any_word', 'true');
        if (isStrictSearch) newParams.set('strict_search', 'true');
        if (selectedSearchField && selectedSearchField !== 'all') newParams.set('search_field', selectedSearchField);

        // Only add field params if they're visible for this domain
        if (currentConfig.showFields.codes && selectedCode) {
            newParams.set('code_id', selectedCode);
        }
        if (selectedDomain === 'code_articles') {
            newParams.set('type', selectedType || 'corpus');
        }
        if (currentConfig.showFields.dates) {
            if (localDateStart) newParams.set('date_start', localDateStart);
            if (localDateEnd) newParams.set('date_end', localDateEnd);
        }
        if (selectedDomain === 'joarticles') {
            if (localJoYear) newParams.set('jo_year', localJoYear);
            if (localJoPublicationDateStart) newParams.set('jo_publication_date_start', localJoPublicationDateStart);
            if (localJoPublicationDateEnd) newParams.set('jo_publication_date_end', localJoPublicationDateEnd);
            if (localJoSignatureDateStart) newParams.set('jo_signature_date_start', localJoSignatureDateStart);
            if (localJoSignatureDateEnd) newParams.set('jo_signature_date_end', localJoSignatureDateEnd);
            if (localJoJournalNum) newParams.set('jo_journal_num', localJoJournalNum);
            if (localJoTextNum) newParams.set('jo_text_num', localJoTextNum);
        }
        if (currentConfig.showFields.juridiction && selectedJuridiction) {
            newParams.set('juridiction_id', selectedJuridiction);
        }
        if (currentConfig.showFields.chambre && selectedChambre) {
            newParams.set('chambre_id', selectedChambre);
        }
        if (currentConfig.showFields.nature && selectedNatures.length > 0) {
            newParams.set('nature_id', selectedNatures.join(','));
        }

        newParams.set('page', '1');

        navigate(`/${i18n.language}/search?${newParams.toString()}`);
    };

    // Reset all filters
    const resetFilters = () => {
        setLocalQuery('');
        setSelectedDomain('code_articles');
        setSelectedDocType('');
        setSelectedCode('');
        setLocalDateStart('');
        setLocalDateEnd('');
        setLocalJoSignatureDateStart('');
        setLocalJoSignatureDateEnd('');
        setLocalJoYear('');
        setLocalJoPublicationDateStart('');
        setLocalJoPublicationDateEnd('');
        setLocalJoJournalNum('');
        setLocalJoTextNum('');
        setSelectedJuridiction('');
        setSelectedChambre('');
        setSelectedNatures([]);
        setIsWholeWord(false);
        setIsAnyWord(false);
        setIsStrictSearch(false);
        setSelectedSearchField('title');
        navigate(`/${i18n.language}/search`);
    };

    // Save current search
    const saveSearch = async () => {
        // Check if any meaningful filter is selected
        const hasQuery = !!localQuery;
        const hasDocType = !!selectedDocType;
        const hasCode = currentConfig.showFields.codes && !!selectedCode;
        const hasDates = currentConfig.showFields.dates && (!!localDateStart || !!localDateEnd);
        const hasJuridiction = currentConfig.showFields.juridiction && !!selectedJuridiction;
        const hasChambre = currentConfig.showFields.chambre && !!selectedChambre;
        const hasNature = currentConfig.showFields.nature && selectedNatures.length > 0;

        // Don't save if no filters are selected
        if (!hasQuery && !hasDocType && !hasCode && !hasDates && !hasJuridiction && !hasChambre && !hasNature) {
            alert(isArabic ? 'يرجى تحديد فلتر واحد على الأقل قبل الحفظ' : 'Veuillez sélectionner au moins un filtre avant de sauvegarder');
            return;
        }

        try {
            const params = new URLSearchParams();
            if (localQuery) params.set('q', localQuery);
            if (selectedDomain) params.set('domain', selectedDomain);
            if (selectedDocType) params.set('doc_type', selectedDocType);
            if (isWholeWord) params.set('whole_word', 'true');
            if (isAnyWord) params.set('any_word', 'true');
            if (currentConfig.showFields.codes && selectedCode) params.set('code_id', selectedCode);
            if (currentConfig.showFields.dates && localDateStart) params.set('date_start', localDateStart);
            if (currentConfig.showFields.dates && localDateEnd) params.set('date_end', localDateEnd);
            if (currentConfig.showFields.juridiction && selectedJuridiction) params.set('juridiction_id', selectedJuridiction);
            if (currentConfig.showFields.chambre && selectedChambre) params.set('chambre_id', selectedChambre);
            if (currentConfig.showFields.nature && selectedNatures.length > 0) params.set('nature_id', selectedNatures.join(','));

            await api.post('/saved-search', {
                query_string: params.toString(),
                doc_type: selectedDomain
            });
            alert(isArabic ? 'تم حفظ البحث بنجاح!' : 'Recherche sauvegardée avec succès!');
        } catch (err: any) {
            if (err.response?.status === 401) {
                alert(isArabic ? 'يرجى تسجيل الدخول لحفظ البحث' : 'Connectez-vous pour sauvegarder la recherche');
            } else {
                alert(isArabic ? 'خطأ في حفظ البحث' : 'Erreur lors de la sauvegarde');
            }
        }
    };

    // Handle domain change - reset related fields
    const handleDomainChange = (newDomain: string) => {
        setSelectedDomain(newDomain);
        // Reset fields that may not be applicable
        setSelectedDocType('');
        setSelectedCode('');
        setLocalDateStart('');
        setLocalDateEnd('');
        setLocalJoSignatureDateStart('');
        setLocalJoSignatureDateEnd('');
        setLocalJoYear('');
        setLocalJoPublicationDateStart('');
        setLocalJoPublicationDateEnd('');
        setLocalJoJournalNum('');
        setLocalJoTextNum('');
        setSelectedJuridiction('');
        setSelectedChambre('');
        setSelectedNatures([]);
    };

    return (
        <div className={`search-filters-panel ${isCollapsed ? 'collapsed' : ''}`}>
            {/* Header */}
            <div className="filters-header">
                <h5>
                    <i className="bi bi-funnel-fill me-2"></i>
                    {isArabic ? 'الفلاتر' : 'FILTRES'}
                </h5>
                <button
                    className="btn btn-sm  collapse-toggle bg-transparent text-light"
                    onClick={() => setIsCollapsed(!isCollapsed)}
                >
                    <i className={`bi bi-chevron-${isCollapsed ? 'down' : 'up'}`}></i>
                </button>
            </div>

            {!isCollapsed && (
                <div className="filters-body">
                    {/* 1. Domaine Juridique - MOVED TO TOP */}
                    <div className="filter-group">
                        <label className="filter-label">
                            <i className="bi bi-collection me-1"></i>
                            {isArabic ? 'المجال القانوني' : 'Domaine Juridique'}
                        </label>
                        <select
                            className="form-select"
                            value={selectedDomain}
                            onChange={(e) => handleDomainChange(e.target.value)}
                        >
                            {domainOptions.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {isArabic ? opt.labelAr : opt.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* 2. Mots-Clés / Expression */}
                    <div className="filter-group">
                        <label className="filter-label">
                            <i className="bi bi-search me-1"></i>
                            {isArabic ? 'الكلمات المفتاحية' : 'Mots-Clés / Expression'}
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            value={localQuery}
                            onChange={(e) => setLocalQuery(e.target.value)}
                            placeholder={isArabic ? 'بحث...' : 'Rechercher...'}
                            onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                        />

                        {/* Search Field Options - Badge Style (no 'Tout') */}
                        <div className="search-mode-badges mt-2">
                            <span className="badge-label">{isArabic ? 'البحث في:' : 'Chercher dans:'}</span>
                            <div className="badge-group">
                                <label className={`search-badge ${selectedSearchField === 'title' ? 'active' : ''}`}>
                                    <input
                                        type="radio"
                                        name="searchField"
                                        checked={selectedSearchField === 'title'}
                                        onChange={() => {
                                            setSelectedSearchField('title');
                                            setIsStrictSearch(true); // Title/Number search is always strict
                                        }}
                                    />
                                    {isArabic
                                        ? (['joarticles', 'livres', 'theses', 'revue_articles'].includes(selectedDomain) ? 'العنوان' : selectedDomain === 'jurisprudences' ? 'رقم القرار' : 'رقم المادة')
                                        : (['joarticles', 'livres', 'theses', 'revue_articles'].includes(selectedDomain) ? 'Titre' : selectedDomain === 'jurisprudences' ? 'Numéro de l\'arrêt' : 'Numéro')
                                    }
                                </label>
                                <label className={`search-badge ${selectedSearchField === 'content' ? 'active' : ''}`}>
                                    <input
                                        type="radio"
                                        name="searchField"
                                        checked={selectedSearchField === 'content'}
                                        onChange={() => {
                                            setSelectedSearchField('content');
                                            setIsStrictSearch(false); // Content search is not strict by default
                                        }}
                                    />
                                    {isArabic ? 'المحتوى' : 'Contenu'}
                                </label>
                                {selectedDomain === 'code_articles' && (
                                    <>
                                        <span className="badge-separator">—</span>
                                        <label className={`search-badge ${selectedType === 'corpus' ? 'active' : ''}`}>
                                            <input
                                                type="radio"
                                                name="codeType"
                                                checked={selectedType === 'corpus'}
                                                onChange={() => setSelectedType('corpus')}
                                            />
                                            {isArabic ? 'المتن' : 'Corpus'}
                                        </label>
                                        <label className={`search-badge ${selectedType === 'annexe' ? 'active' : ''}`}>
                                            <input
                                                type="radio"
                                                name="codeType"
                                                checked={selectedType === 'annexe'}
                                                onChange={() => setSelectedType('annexe')}
                                            />
                                            {isArabic ? 'الملحق' : 'Annexe'}
                                        </label>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* 4. Code Juridique - ONLY for code_articles */}
                    {currentConfig.showFields.codes && (
                        <div className="filter-group">
                            <label className="filter-label">
                                <i className="bi bi-book me-1"></i>
                                {isArabic ? 'القانون' : 'Code Juridique'}
                            </label>
                            <select
                                id="code-select"
                                className="form-select"
                                value={selectedCode}
                                onChange={(e) => setSelectedCode(e.target.value)}
                                style={{ display: 'none' }}
                            >
                                <option value="">{isArabic ? 'جميع القوانين' : 'Tous les codes'}</option>
                                {availableCodes.map(code => (
                                    <option key={code.id} value={code.id}>
                                        {isArabic && code.name_ar ? code.name_ar : code.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* 5. Période de Date - CONDITIONAL: only for jurisprudences and joarticles */}
                    {currentConfig.showFields.dates && (
                        <div className="filter-group">
                            <label className="filter-label">
                                <i className="bi bi-calendar-range me-1"></i>
                                {selectedDomain === 'jurisprudences'
                                    ? (isArabic ? 'تاريخ القرار' : "Date de l'arrêt")
                                    : (isArabic ? 'تاريخ النشر' : 'Date de publication')
                                }
                            </label>
                            <div className="date-range">
                                <input
                                    type="date"
                                    className="form-control"
                                    value={localDateStart}
                                    onChange={(e) => setLocalDateStart(e.target.value)}
                                />
                                <span className="date-separator">→</span>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={localDateEnd}
                                    onChange={(e) => setLocalDateEnd(e.target.value)}
                                />
                            </div>
                        </div>
                    )}

                    {/* 5b. JO-specific filters: publication (fixed) + signature (range) + journal number */}
                    {selectedDomain === 'joarticles' && (
                        <>
                            <div className="filter-group">
                                <label className="filter-label">
                                    <i className="bi bi-calendar-check me-1"></i>
                                    {isArabic ? 'سنة النشر' : 'Année de publication'}
                                </label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={localJoYear}
                                    onChange={(e) => setLocalJoYear(e.target.value)}
                                    placeholder="Ex: 2023"
                                    min="1962"
                                    max="2100"
                                    onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                                />
                            </div>
                            <div className="filter-group">
                                <label className="filter-label">
                                    <i className="bi bi-calendar-range me-1"></i>
                                    {isArabic ? 'تاريخ النشر' : 'Date de publication'}
                                </label>
                                <div className="date-range">
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={localJoPublicationDateStart}
                                        onChange={(e) => setLocalJoPublicationDateStart(e.target.value)}
                                    />
                                    <span className="date-separator">→</span>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={localJoPublicationDateEnd}
                                        onChange={(e) => setLocalJoPublicationDateEnd(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="filter-group">
                                <label className="filter-label">
                                    <i className="bi bi-calendar-event me-1"></i>
                                    {isArabic ? 'تاريخ التوقيع' : 'Date de signature'}
                                </label>
                                <div className="date-range">
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={localJoSignatureDateStart}
                                        onChange={(e) => setLocalJoSignatureDateStart(e.target.value)}
                                    />
                                    <span className="date-separator">→</span>
                                    <input
                                        type="date"
                                        className="form-control"
                                        value={localJoSignatureDateEnd}
                                        onChange={(e) => setLocalJoSignatureDateEnd(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="filter-group">
                                <label className="filter-label">
                                    <i className="bi bi-hash me-1"></i>
                                    {isArabic ? 'رقم الجريدة' : 'Numéro du journal'}
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={localJoJournalNum}
                                    onChange={(e) => setLocalJoJournalNum(e.target.value)}
                                    placeholder={isArabic ? 'رقم الجريدة الرسمية' : 'Ex: 42'}
                                    onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                                />
                            </div>
                            <div className="filter-group">
                                <label className="filter-label">
                                    <i className="bi bi-file-earmark-text me-1"></i>
                                    {isArabic ? 'رقم النص الرسمي' : 'Numéro du texte officiel'}
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={localJoTextNum}
                                    onChange={(e) => setLocalJoTextNum(e.target.value)}
                                    placeholder={isArabic ? 'رقم النص' : 'Ex: 12-34'}
                                    onKeyPress={(e) => e.key === 'Enter' && applyFilters()}
                                />
                            </div>
                        </>
                    )}

                    {/* 6. Organe / Juridiction - ONLY for jurisprudences */}
                    {currentConfig.showFields.juridiction && (
                        <div className="filter-group">
                            <label className="filter-label">
                                <i className="bi bi-building me-1"></i>
                                {isArabic ? 'الجهة / المحكمة' : 'Organe / Juridiction'}
                            </label>
                            <select
                                className="form-select"
                                value={selectedJuridiction}
                                onChange={(e) => setSelectedJuridiction(e.target.value)}
                            >
                                <option value="">{isArabic ? 'جميع المحاكم' : 'Toutes les juridictions'}</option>
                                {availableJuridictions.map(j => (
                                    <option key={j.id} value={j.id}>
                                        {isArabic && j.name_ar ? j.name_ar : j.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* 7. Chambre - ONLY for jurisprudences */}
                    {currentConfig.showFields.chambre && availableChambres.length > 0 && (
                        <div className="filter-group">
                            <label className="filter-label">
                                <i className="bi bi-door-open me-1"></i>
                                {isArabic ? 'الغرفة' : 'Chambre'}
                            </label>
                            <select
                                className="form-select"
                                value={selectedChambre}
                                onChange={(e) => setSelectedChambre(e.target.value)}
                            >
                                <option value="">{isArabic ? 'جميع الغرف' : 'Toutes les chambres'}</option>
                                {availableChambres.map(c => (
                                    <option key={c.id} value={c.id}>
                                        {isArabic && c.name_ar ? c.name_ar : c.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* 8. Nature du Texte - ONLY for joarticles */}
                    {currentConfig.showFields.nature && availableNatures.length > 0 && (
                        <div className="filter-group">
                            <label className="filter-label">
                                <i className="bi bi-tag me-1"></i>
                                {isArabic ? 'نوع النص' : 'Nature du texte'}
                            </label>
                            <select
                                id="nature-select"
                                multiple
                                data-placeholder={isArabic ? 'اختر نوع النص...' : 'Sélectionner la nature...'}
                                className="form-select"
                                value={selectedNatures.map(String)}
                                onChange={(e) => {
                                    // This onChange might not be triggered by TomSelect directly, 
                                    // we usually listen to the instance, but let's keep the select sync
                                    const options = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                                    setSelectedNatures(options);
                                }}
                                style={{ display: 'none' }} // TomSelect hides it, but explicit hide helps prevent flash
                            >
                                {availableNatures.map(nature => (
                                    <option key={nature.id} value={nature.id}>
                                        {isArabic && nature.name_ar ? nature.name_ar : nature.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
 
                    {/* Action Buttons */}
                    <div className="filter-actions">
                        {codePdfUrl && selectedCode && (
                            <>
                                <button
                                    className="btn btn-info"
                                    onClick={() => navigate(`/${i18n.language}/pdf/code/${selectedCode}`)}
                                >
                                    <i className="bi bi-book me-2"></i>
                                    {isArabic ? 'الكتاب الالكتروني' : 'eBook du code'}
                                </button>
                            </>
                        )}
                        <button
                            className="btn btn-primary btn-apply"
                            onClick={applyFilters}
                        >
                            <i className="bi bi-funnel me-2"></i>
                            {isArabic ? 'تطبيق الفلاتر' : 'APPLIQUER LES FILTRES'}
                        </button>
                        <button
                            className="btn btn-outline-success btn-save"
                            onClick={saveSearch}
                        >
                            <i className="bi bi-bookmark-plus me-1"></i>
                            {isArabic ? 'حفظ البحث' : 'Sauvegarder la recherche'}
                        </button>
                        <button
                            className="btn btn-outline-secondary btn-reset"
                            onClick={resetFilters}
                        >
                            <i className="bi bi-arrow-counterclockwise me-1"></i>
                            {isArabic ? 'إعادة تعيين' : 'Réinitialiser'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchFilters;
